<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {Model} from "@/app/Model";
import Chart from 'chart.js/auto';
import Choices from "choices.js";

import {CommsFamiglia, type IEsito} from "@/app/CommsFamiglia";

import * as bootstrap from 'bootstrap';
import MinMaxSlider from '@/components/atomics/MinMaxSliderDashboard.vue';
import PaginazioneDashboard from "@/components/atomics/PaginazioneDashboardFamiglia.vue";
import PaginazioneDashboardJump from "@/components/atomics/PaginazioneDashboardFamigliaJump.vue";
import type {KeyValoreStr, KeyValoreStrNum, KeyValoreStrNumArr, KeyValoreStrNumBool} from "@/app/data/DataTypes";
import {PdfGenerator} from "@/app/PdfGenerator";

declare let gtagO: Function;

let model: Model = Model.getReactiveInstance();

let filtersHtml = ref("");

let chartOggettoDiGiudizio: Chart | null = null;
let chartTipologiaProcedimento: Chart | null = null;
let chartAddebito: Chart | null = null;
let chartMotiviAddebitoSeparazione: Chart | null = null;
let chartRegimeAffidamento: Chart | null = null;
let chartRegimeMantenimento: Chart | null = null;
let chartRegimeCollocamento: Chart | null = null;
let chartAssegnazioneCasaFamiliare: Chart | null = null;
let chartDurataProcessoSingolaCitta: Chart | null = null;
let chartOggettoGiudizioCitta: Chart | null = null;

let choiceCitta: Choices | null = null;
let choiceGiudice: Choices | null = null;

const CHART_COLORS = {
  gfl1: 'rgb(67,172,239)',
  gfl2: 'rgb(22, 91, 170)',
  gfl3: 'rgb(150,216,246)',
  gfl4: 'rgb(19, 51, 113)',
  gfl5: 'rgb(227, 242, 253)',
  gfl6: 'rgb(41, 98, 255)',
  gfl7: 'rgb(161, 85, 185)',
  gfl8: 'rgb(22, 191, 214)'
};

// START ----------------------
onMounted(() => {
  checkStart();
});
function checkStart(): void {
  if (!Model.fam_setupped) {
    setTimeout(() => checkStart(), 100)
  } else if (!CommsFamiglia.listAllDone) {
    CommsFamiglia.getInstance().listAll(() => {
      checkStart();
    });
  } else {
    initComponents();
  }
}

// MAIN ----------------------
function changeDashboardAt(field: string = ""): void {
  let valueField = "";
  let stringField = field;

  switch (field) {
    case "oggetto_di_giudizio":
      valueField = model.fam_flow_dashboard.oggetto_di_giudizio;
      stringField = "oggetto del giudizio";

      break;
    case "tipologia_procedimento":
      valueField = model.fam_flow_dashboard.tipologia_procedimento;
      stringField = "tipologia procedimento";

      break;
    case "addebito":
      valueField = model.fam_flow_dashboard.addebito + "";
      break;
    case "motivi_addebito_separazione":
      valueField = model.fam_flow_dashboard.motivi_addebito_separazione;
      stringField = "motivi di addebito della separazione";

      break;
    case "regime_affidamento":
      valueField = model.fam_flow_dashboard.regime_affidamento;
      stringField = "regime di affidamento";

      break;
    case "regime_mantenimento":
      valueField = model.fam_flow_dashboard.regime_mantenimento;
      stringField = "regime di mantenimento";

      break;
    case "regime_collocamento":
      valueField = model.fam_flow_dashboard.regime_collocamento;
      stringField = "regime di collocamento";

      break;
    case "assegnazione_casa_familiare":
      valueField = model.fam_flow_dashboard.assegnazione_casa_familiare;
      stringField = "assegnazione casa coniugale";

      break;
    case "valore_assegno_mantenimento":
      valueField = model.fam_flow_dashboard.valore_assegno_mantenimento_da + " - " + model.fam_flow_dashboard.valore_assegno_mantenimento_a;
      stringField = "valore assegno mantenimento";
      
      break;
    case "valore_assegno_contributo_mantenimento":
      valueField = model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_da + " - " + model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_a;
      stringField = "valore assegno contributo mantenimento";

      break;
    case "valore_assegno_divorzile":
      valueField = model.fam_flow_dashboard.valore_assegno_divorzile_da + " - " + model.fam_flow_dashboard.valore_assegno_divorzile_a;
      stringField = "valore assegno divorzio";

      break;
    case "id_localita":
      valueField = model.fam_flow_dashboard.id_localita;
      stringField = "città";

      if (valueField) {
        let inputLocalita: HTMLElement | null = document.getElementById("input_id_localita");
        let optionLocalita = null;

        if (inputLocalita) {
          optionLocalita = inputLocalita.querySelectorAll("option");

          if (optionLocalita) {
            for (let i = 0; i < optionLocalita.length; i++) {
              if (optionLocalita[i].selected == true) {
                valueField = optionLocalita[i].innerHTML;
              }
            }
          }
        }
      }

      break;
    case "durata_del_processo":
      valueField = model.fam_flow_dashboard.durata_del_processo + "";
      stringField = "durata del processo";

      break;
    case "data":
      valueField = model.fam_flow_dashboard.data_da + " - " + model.fam_flow_dashboard.data_a;
      stringField = "anno";

      break;
    case "giudice":
      valueField = model.fam_flow_dashboard.giudice;
      break;
  }

  if (valueField) {
    gtagO({
      event: 'filtro',
      filtro: stringField,
      valore: valueField,
      percorso: 'Famiglia'
    })
  }

  callQuery();
}

function clearAllFilters(): void {
  model.fam_flow_dashboard.reset();
  
  changeDashboardAt();
}

function clearSingleFilter(field: string): void {
  let canUpdate: boolean = true;

  switch (field) {
    case "":
      canUpdate = false;
      break;
    case "durata_del_processo_notNull":
      model.fam_flow_dashboard.durata_del_processo_notNull = false;
      break;
    case "durata_del_processo_active":
      model.fam_flow_dashboard.durata_del_processo_active = false;
      model.fam_flow_dashboard.durata_del_processo = 0;
      break;
    case "valore_assegno_mantenimento_active":
      model.fam_flow_dashboard.valore_assegno_mantenimento_active = false;

      model.fam_flow_dashboard.valore_assegno_mantenimento_da = model.fam_flow_dashboard.valore_assegno_mantenimento_min;
      model.fam_flow_dashboard.valore_assegno_mantenimento_a = model.fam_flow_dashboard.valore_assegno_mantenimento_max;
      break;
    case "valore_assegno_contributo_mantenimento_active":
      model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_active = false;

      model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_da = model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_min;
      model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_a = model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_max;
      break;
    case "valore_assegno_divorzile_active":
      model.fam_flow_dashboard.valore_assegno_divorzile_active = false;

      model.fam_flow_dashboard.valore_assegno_divorzile_da = model.fam_flow_dashboard.valore_assegno_divorzile_min;
      model.fam_flow_dashboard.valore_assegno_divorzile_a = model.fam_flow_dashboard.valore_assegno_divorzile_max;
      break;
    case "data_active":
      model.fam_flow_dashboard.data_active = false;

      model.fam_flow_dashboard.data_da = model.fam_flow_dashboard.data_min;
      model.fam_flow_dashboard.data_a = model.fam_flow_dashboard.data_max;
      break;
    default:
      //@ts-ignore
      model.fam_flow_dashboard[field] = "";
      break;
  }

  if (canUpdate) changeDashboardAt();
}

//@ts-ignore
window.clearSingleFilter = clearSingleFilter;

function callQueryPaginazione(): void {
  callQuery(true);
}
function callQuery(soloPaginazione: boolean = false): void {
  //chiama i dati attuali
  CommsFamiglia.getInstance().queryDashboard((e: IEsito) => {
    updateComponents();
  }, soloPaginazione);
}

// COMPONENTS INIT ----------------------
function initComponents(): void {
  //SIDEBAR
  model.sidebarCollapsed = false;

  //CHOICES
  initChoices();

  //TOOLTIPS
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  //@ts-ignore
  const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl))

  //LOAD DATA
  callQuery();
}

function initChoices(): void {
  createChoiceCitta();
  createChoiceGiudice();
}

function createChoiceCitta(): void {
  if (choiceCitta) choiceCitta.destroy();

  let choiceItems: any[] = [{
    value: "",
    label: "Tutti...",
    selected: model.fam_flow_dashboard.id_localita != "" ? false : true,
    disabled: false
  }];

  let dataSourceCitta = model.fam_lista_citta;
  if (model.fam_get_dashboard.totale_citta && model.fam_get_dashboard.totale_citta.length > 0) dataSourceCitta = model.fam_get_dashboard.totale_citta;
  dataSourceCitta.forEach(x => choiceItems.push({
    value: x.id_localita,
    label: x.citta,
    selected: false,
    disabled: false
  }));

  let defaultItem: any = {
    value: "",
    label: "Tutti...",
    selected: true,
    disabled: false
  }

  if (model.fam_flow_dashboard.id_localita != "") {
    defaultItem.value = model.fam_flow_dashboard.id_localita;

    let useLabel: string = model.fam_flow_dashboard.id_localita;
    let findCitta: KeyValoreStr | undefined = dataSourceCitta.find( (x: KeyValoreStr): boolean => { return x.id_localita == model.fam_flow_dashboard.id_localita });

    if (findCitta) useLabel = findCitta.citta;
    defaultItem.label = useLabel;
  }

  let itemItems: any[] = [defaultItem];

  //@ts-ignore
  choiceCitta = new Choices(document.getElementById('input_id_localita'), {
    items: itemItems,
    choices: choiceItems,
    allowHTML: true,
    placeholder: false,
    loadingText: 'Caricamento...',
    noResultsText: 'Nessun risultato',
    noChoicesText: 'Nessun elemento da selezionare',
    itemSelectText: 'Premi per selezionare',
  });
}

function createChoiceGiudice(): void {
  if (choiceGiudice) choiceGiudice.destroy();

  let choiceItems: any[] = [{
    value: "",
    label: "Tutti...",
    selected: model.fam_flow_dashboard.giudice != "" ? false : true,
    disabled: false
  }];

  let dataSourceGiudici = model.fam_lista_giudici;
  if (model.fam_get_dashboard.totale_giudici && model.fam_get_dashboard.totale_giudici.length > 0) dataSourceGiudici = model.fam_get_dashboard.totale_giudici;
  dataSourceGiudici.forEach(x => choiceItems.push({
    value: x.giudice,
    label: x.giudice,
    selected: false,
    disabled: false
  }));

  let defaultItem: any = {
    value: "",
    label: "Tutti...",
    selected: true,
    disabled: false
  }

  if (model.fam_flow_dashboard.giudice != "") {
    defaultItem.value = model.fam_flow_dashboard.giudice;
    defaultItem.label = model.fam_flow_dashboard.giudice;
  }

  let itemItems: any[] = [defaultItem];

  //@ts-ignore
  choiceGiudice = new Choices(document.getElementById('input_giudice'), {
    items: itemItems,
    choices: choiceItems,
    allowHTML: true,
    placeholder: false,
    loadingText: 'Caricamento...',
    noResultsText: 'Nessun risultato',
    noChoicesText: 'Nessun elemento da selezionare',
    itemSelectText: 'Premi per selezionare',
  });
}

// COMPONENTS UPDATE ----------------------
function updateComponents(): void {
  //CHARTS
  updateCharts();
  //FILTRI
  updateFilters();
  //CHOICES
  updateChoices();
  //MINMAX
  updateMinMaxSliders(true);
  //MAP
  updateMap();
  //PAGINAZIONE
  updatePaginazione();
}

let chartOggettoDiGiudizioExists: boolean = false;
let chartTipologiaProcedimentoExists: boolean = false;
let chartAddebitoExists: boolean = false;
let chartMotiviAddebitoSeparazioneExists: boolean = false;
let chartRegimeAffidamentoExists: boolean = false;
let chartRegimeMantenimentoExists: boolean = false;
let chartRegimeCollocamentoExists: boolean = false;
let chartAssegnazioneCasaFamiliareExists: boolean = false;
let chartDurataProcessoSingolaCittaExists: boolean = false;
let chartOggettoGiudizioCittaExists: boolean = false;

function updateCharts(): void {
  updateChartsOggettoDiGiudizio();
  updateChartsTipologiaProcedimento();
  updateChartsAddebito();
  updateChartsMotiviAddebitoSeparazione();
  updateChartsRegimeAffidamento();
  updateChartsRegimeMantenimento();
  updateChartsRegimeCollocamento();
  updateChartsAssegnazioneCasaFamiliare();
  updateChartsDurataProcessoSingolaCitta();
  updateChartsOggettoGiudizioCitta();
}

const getOrCreateLegendList = (chart: Chart, id:string): HTMLElement | null => {
  const legendContainer: HTMLElement | null = document.getElementById(id);
  let listContainer: HTMLElement | null = null;
  if (legendContainer) {
    listContainer = legendContainer.querySelector('ul');

    if (!listContainer) {
      listContainer = document.createElement('ul');
      listContainer.style.display = 'flex';
      listContainer.style.flexDirection = 'row';
      listContainer.style.margin = "0";
      listContainer.style.padding = "0";

      legendContainer.appendChild(listContainer);
    }
  }

  return listContainer;
};

const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart: any, args: any, options: any) {
    const ul = getOrCreateLegendList(chart, options.containerID);
    if (ul) {
      // Remove old legend items
      while (ul.firstChild) {
        ul.firstChild.remove();
      }
    }

    // Reuse the built-in legendItems generator
    const items = chart.options.plugins.legend.labels.generateLabels(chart);

    items.forEach( (item: any) => {
      const li = document.createElement('li');
      li.style.alignItems = 'center';
      li.style.cursor = 'auto';
      li.style.display = 'flex';
      li.style.flexDirection = 'row';
      li.style.marginLeft = '10px';

      // Color box
      const boxSpan = document.createElement('span');
      boxSpan.style.background = item.fillStyle;
      boxSpan.style.borderColor = item.strokeStyle;
      boxSpan.style.borderWidth = item.lineWidth + 'px';
      boxSpan.style.display = 'inline-block';
      boxSpan.style.flexShrink = "0";
      boxSpan.style.height = '20px';
      boxSpan.style.marginRight = '10px';
      boxSpan.style.width = '20px';

      // Text
      const textContainer = document.createElement('p');
      textContainer.style.color = item.fontColor;
      textContainer.style.margin = "0";
      textContainer.style.padding = "0";
      textContainer.style.textDecoration = item.hidden ? 'line-through' : '';

      const text = document.createTextNode(item.text);
      textContainer.appendChild(text);

      li.appendChild(boxSpan);
      li.appendChild(textContainer);
      if (ul) ul.appendChild(li);
    });
  }
};

function updateChartsOggettoDiGiudizio(): void {
  if (Chart.getChart("canvas_oggetto_di_giudizio")) {
    Chart.getChart("canvas_oggetto_di_giudizio")?.destroy();
  }

  if (model.fam_get_dashboard.oggetto_di_giudizio.length > 0) {
    const configOggettoDiGiudizio = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [{
          label: 'Oggetto del giudizio',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_oggetto_di_giudizio',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    };

    model.fam_get_dashboard.oggetto_di_giudizio.forEach((x: KeyValoreStrNum) => {
      //@ts-ignore
      configOggettoDiGiudizio.data.labels.push(x.oggetto_di_giudizio + " (" + x.percentuale + "%)");

      //@ts-ignore
      configOggettoDiGiudizio.data.datasets[0].data.push(x.percentuale);
    });

    let canvasOggettoDiGiudizio: HTMLElement | null = document.getElementById('canvas_oggetto_di_giudizio');

    //@ts-ignore
    chartOggettoDiGiudizio = new Chart(canvasOggettoDiGiudizio, configOggettoDiGiudizio);

    if (!chartOggettoDiGiudizioExists) {
      chartOggettoDiGiudizioExists = true;

      //@ts-ignore
      canvasOggettoDiGiudizio.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartOggettoDiGiudizio.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("oggetto_di_giudizio", chartOggettoDiGiudizio.data.labels[res[0].index]);
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_oggetto_di_giudizio");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsTipologiaProcedimento(): void {
  if (Chart.getChart("canvas_tipologia_procedimento")) {
    Chart.getChart("canvas_tipologia_procedimento")?.destroy();
  }

  if (model.fam_get_dashboard.tipologia_procedimento && model.fam_get_dashboard.tipologia_procedimento.length > 0) {
    const configTipologiaProcedimento = {
      type: "doughnut",
      data: {
        labels: [],
        datasets: [{
          label: 'Tipologia Procedimento',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_tipologia_procedimento',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    }

    model.fam_get_dashboard.tipologia_procedimento.forEach((x: KeyValoreStrNumArr) => {
      //@ts-ignore
      configTipologiaProcedimento.data.labels.push(x.tipologia_procedimento + " (" + x.percentuale + "%)");

      //@ts-ignore
      configTipologiaProcedimento.data.datasets[0].data.push(x.percentuale);
    });

    let canvasTipologiaProcedimento: HTMLElement | null = document.getElementById('canvas_tipologia_procedimento');

    //@ts-ignore
    chartTipologiaProcedimento = new Chart(canvasTipologiaProcedimento, configTipologiaProcedimento);

    if (!chartTipologiaProcedimentoExists) {
      chartTipologiaProcedimentoExists = true;

      //@ts-ignore
      canvasTipologiaProcedimento.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartTipologiaProcedimento.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("tipologia_procedimento", chartTipologiaProcedimento.data.labels[res[0].index])
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_tipologia_procedimento");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsAddebito(): void {
  if (Chart.getChart("canvas_addebito")) {
    Chart.getChart("canvas_addebito")?.destroy();
  }

  if (model.fam_get_dashboard.addebito && model.fam_get_dashboard.addebito.length > 0) {
    const configAddebito = {
      type: "doughnut",
      data: {
        labels: [],
        datasets: [{
          label: 'Addebito',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_addebito',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    }

    model.fam_get_dashboard.addebito.forEach((x: KeyValoreStrNumBool) => {
      //@ts-ignore
      configAddebito.data.labels.push((x.addebito ? "Si" : "No") + " (" + x.percentuale + "%)");

      //@ts-ignore
      configAddebito.data.datasets[0].data.push(x.percentuale);
    });

    let canvasAddebito: HTMLElement | null = document.getElementById('canvas_addebito');

    //@ts-ignore
    chartAddebito = new Chart(canvasAddebito, configAddebito);

    if (!chartAddebitoExists) {
      chartAddebitoExists = true;

      //@ts-ignore
      canvasAddebito.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartAddebito.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("addebito", chartAddebito.data.labels[res[0].index]);
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_addebito");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsMotiviAddebitoSeparazione(): void {
  if (Chart.getChart("canvas_motivi_addebito_separazione")) {
    Chart.getChart("canvas_motivi_addebito_separazione")?.destroy();
  }

  if (model.fam_get_dashboard.motivi_addebito_separazione.length > 0) {
    const configMotiviAddebitoSeparazione = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [{
          label: 'Motivi di addebito della separazione ',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_motivi_addebito_separazione',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    };

    model.fam_get_dashboard.motivi_addebito_separazione.forEach((x: KeyValoreStrNum) => {
      //@ts-ignore
      configMotiviAddebitoSeparazione.data.labels.push(x.motivi_addebito_separazione + " (" + x.percentuale + "%)");

      //@ts-ignore
      configMotiviAddebitoSeparazione.data.datasets[0].data.push(x.percentuale);
    });

    let canvasMotiviAddebitoSeparazione: HTMLElement | null = document.getElementById('canvas_motivi_addebito_separazione');

    //@ts-ignore
    chartMotiviAddebitoSeparazione = new Chart(canvasMotiviAddebitoSeparazione, configMotiviAddebitoSeparazione);

    if (!chartMotiviAddebitoSeparazioneExists) {
      chartMotiviAddebitoSeparazioneExists = true;

      //@ts-ignore
      canvasMotiviAddebitoSeparazione.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartMotiviAddebitoSeparazione.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("motivi_addebito_separazione", chartMotiviAddebitoSeparazione.data.labels[res[0].index]);
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_motivi_addebito_separazione");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsRegimeAffidamento(): void {
  if (Chart.getChart("canvas_regime_affidamento")) {
    Chart.getChart("canvas_regime_affidamento")?.destroy();
  }

  if (model.fam_get_dashboard.regime_affidamento.length > 0) {
    const configRegimeAffidamento = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [{
          label: 'Regime di affidamento',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_regime_affidamento',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    };

    model.fam_get_dashboard.regime_affidamento.forEach((x: KeyValoreStrNum) => {
      //@ts-ignore
      configRegimeAffidamento.data.labels.push(x.regime_affidamento + " (" + x.percentuale + "%)");

      //@ts-ignore
      configRegimeAffidamento.data.datasets[0].data.push(x.percentuale);
    });

    let canvasRegimeAffidamento: HTMLElement | null = document.getElementById('canvas_regime_affidamento');

    //@ts-ignore
    chartRegimeAffidamento = new Chart(canvasRegimeAffidamento, configRegimeAffidamento);

    if (!chartRegimeAffidamentoExists) {
      chartRegimeAffidamentoExists = true;

      //@ts-ignore
      canvasRegimeAffidamento.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartRegimeAffidamento.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("regime_affidamento", chartRegimeAffidamento.data.labels[res[0].index]);
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_regime_affidamento");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsRegimeMantenimento(): void {
  if (Chart.getChart("canvas_regime_mantenimento")) {
    Chart.getChart("canvas_regime_mantenimento")?.destroy();
  }

  if (model.fam_get_dashboard.regime_mantenimento.length > 0) {
    const configRegimeMantenimento = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [{
          label: 'Regime di mantenimento',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_regime_mantenimento',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    };

    model.fam_get_dashboard.regime_mantenimento.forEach((x: KeyValoreStrNum) => {
      //@ts-ignore
      configRegimeMantenimento.data.labels.push(x.regime_mantenimento + " (" + x.percentuale + "%)");

      //@ts-ignore
      configRegimeMantenimento.data.datasets[0].data.push(x.percentuale);
    });

    let canvasRegimeMantenimento: HTMLElement | null = document.getElementById('canvas_regime_mantenimento');

    //@ts-ignore
    chartRegimeMantenimento = new Chart(canvasRegimeMantenimento, configRegimeMantenimento);

    if (!chartRegimeMantenimentoExists) {
      chartRegimeMantenimentoExists = true;

      //@ts-ignore
      canvasRegimeMantenimento.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartRegimeMantenimento.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("regime_mantenimento", chartRegimeMantenimento.data.labels[res[0].index]);
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_regime_mantenimento");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsRegimeCollocamento(): void {
  if (Chart.getChart("canvas_regime_collocamento")) {
    Chart.getChart("canvas_regime_collocamento")?.destroy();
  }

  if (model.fam_get_dashboard.regime_collocamento.length > 0) {
    const configRegimeCollocamento = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [{
          label: 'Regime di collocamento',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_regime_collocamento',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    };

    model.fam_get_dashboard.regime_collocamento.forEach((x: KeyValoreStrNum) => {
      //@ts-ignore
      configRegimeCollocamento.data.labels.push(x.regime_collocamento + " (" + x.percentuale + "%)");

      //@ts-ignore
      configRegimeCollocamento.data.datasets[0].data.push(x.percentuale);
    });

    let canvasRegimeCollocamento: HTMLElement | null = document.getElementById('canvas_regime_collocamento');

    //@ts-ignore
    chartRegimeCollocamento = new Chart(canvasRegimeCollocamento, configRegimeCollocamento);

    if (!chartRegimeCollocamentoExists) {
      chartRegimeCollocamentoExists = true;

      //@ts-ignore
      canvasRegimeCollocamento.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartRegimeCollocamento.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("regime_collocamento", chartRegimeCollocamento.data.labels[res[0].index]);
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_regime_collocamento");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsAssegnazioneCasaFamiliare(): void {
  if (Chart.getChart("canvas_assegnazione_casa_familiare")) {
    Chart.getChart("canvas_assegnazione_casa_familiare")?.destroy();
  }

  if (model.fam_get_dashboard.assegnazione_casa_familiare.length > 0) {
    const configAssegnazioneCasaFamiliare = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [{
          label: 'Assegnazione casa coniugale',
          data: [],
          backgroundColor: Object.values(CHART_COLORS),
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container_assegnazione_casa_familiare',
          },
          legend: {
            display: false
          }
        },
        radius: "85%"
      },
      plugins: [htmlLegendPlugin]
    };

    model.fam_get_dashboard.assegnazione_casa_familiare.forEach((x: KeyValoreStrNum) => {
      //@ts-ignore
      configAssegnazioneCasaFamiliare.data.labels.push(x.assegnazione_casa_familiare + " (" + x.percentuale + "%)");

      //@ts-ignore
      configAssegnazioneCasaFamiliare.data.datasets[0].data.push(x.percentuale);
    });

    let canvasAssegnazioneCasaFamiliare: HTMLElement | null = document.getElementById('canvas_assegnazione_casa_familiare');

    //@ts-ignore
    chartAssegnazioneCasaFamiliare = new Chart(canvasAssegnazioneCasaFamiliare, configAssegnazioneCasaFamiliare);

    if (!chartAssegnazioneCasaFamiliareExists) {
      chartAssegnazioneCasaFamiliareExists = true;

      //@ts-ignore
      canvasAssegnazioneCasaFamiliare.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartAssegnazioneCasaFamiliare.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("assegnazione_casa_familiare", chartAssegnazioneCasaFamiliare.data.labels[res[0].index]);
      };
    }
  } else {
    const divLegend: HTMLElement | null = document.getElementById("legend-container_assegnazione_casa_familiare");
    if (divLegend) {
      divLegend.innerHTML = "";
    }
  }
}

function updateChartsDurataProcessoSingolaCitta(): void {
  if (Chart.getChart("canvas_durata_del_processo_per_singola_citta")) {
    Chart.getChart("canvas_durata_del_processo_per_singola_citta")?.destroy();
  }

  if (model.fam_get_dashboard.durata_del_processo_per_singola_citta && model.fam_get_dashboard.durata_del_processo_per_singola_citta.length > 0) {
    const configDurataProcessoSingolaCitta = {
      type: "bar",
      data: {
        labels: [],
        datasets: [
          {
            label: "Durata",
            backgroundColor: Object.values(CHART_COLORS),
            data: [],
          }
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
            onClick: null
          }
        },
        barRoundness: 1,
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true
          }
        }
      }
    };

    model.fam_get_dashboard.durata_del_processo_per_singola_citta.forEach((x: KeyValoreStrNum) => {
      //@ts-ignore
      configDurataProcessoSingolaCitta.data.labels.push(x.descr_provincia);
      //@ts-ignore
      configDurataProcessoSingolaCitta.data.datasets[0].data.push(x.durata_del_processo);
    });

    let canvasDurataProcessoSingolaCitta: HTMLElement | null = document.getElementById('canvas_durata_del_processo_per_singola_citta');

    //@ts-ignore
    chartDurataProcessoSingolaCitta = new Chart(canvasDurataProcessoSingolaCitta, configDurataProcessoSingolaCitta);

    if (!chartDurataProcessoSingolaCittaExists) {
      chartDurataProcessoSingolaCittaExists = true;

      //@ts-ignore
      canvasDurataProcessoSingolaCitta.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartDurataProcessoSingolaCitta.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("id_localita", chartDurataProcessoSingolaCitta.data.labels[res[0].index]);
      };
    }
  }
}

function updateChartsOggettoGiudizioCitta(): void {
  if (Chart.getChart("canvas_esito_del_processo_per_citta")) {
    Chart.getChart("canvas_esito_del_processo_per_citta")?.destroy();
  }

  if (model.fam_get_dashboard.esito_del_processo_per_citta && model.fam_get_dashboard.esito_del_processo_per_citta.length > 0) {
    const configOggettoGiudizioCitta = {
      type: "bar",
      data: {
        labels: [],
        datasets: [
          {
            label: Model.lista_oggetto_giudizio_citta_labels[0],
            backgroundColor: CHART_COLORS.gfl1,
            data: [],
          }, {
            label: Model.lista_oggetto_giudizio_citta_labels[1],
            backgroundColor: CHART_COLORS.gfl2,
            data: [],
          }
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            onClick: null
          }
        },
        barRoundness: 1,
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true
          }
        }
      }
    };

    model.fam_get_dashboard.esito_del_processo_per_citta.forEach((x: KeyValoreStrNumArr) => {
      //@ts-ignore
      configOggettoGiudizioCitta.data.labels.push(x.descr_provincia);

      let oggettoDiGiudizio: string[] = (x.oggetto_di_giudizio as string[]);

      for (let odgIndx: number = 0; odgIndx < oggettoDiGiudizio.length; odgIndx++) {
        let label: string = oggettoDiGiudizio[odgIndx];
        let value: number = (x.percentuale as number[])[odgIndx];

        //@ts-ignore
        let indx: number = Model.lista_oggetto_giudizio_citta_labels.indexOf(label);

        //@ts-ignore
        configOggettoGiudizioCitta.data.datasets[indx].data.push(value);
      };
    });

    let canvasOggettoGiudizioCitta: HTMLElement | null = document.getElementById('canvas_esito_del_processo_per_citta');

    //@ts-ignore
    chartOggettoGiudizioCitta = new Chart(canvasOggettoGiudizioCitta, configOggettoGiudizioCitta);

    if (!chartOggettoGiudizioCittaExists) {
      chartOggettoGiudizioCittaExists = true;

      //@ts-ignore
      canvasOggettoGiudizioCitta.onclick = (evt: Event) => {
        //@ts-ignore
        const res = chartOggettoGiudizioCitta.getElementsAtEventForMode(
            evt,
            'nearest',
            {intersect: true},
            true
        );
        if (res.length === 0) {
          return;
        }

        //@ts-ignore
        clickOnChart("id_localita", chartOggettoGiudizioCitta.data.labels[res[0].index]);
      };
    }
  }
}

function updateFilters(): void {
  filtersHtml.value = "";

  if (model.fam_flow_dashboard.oggetto_di_giudizio != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Oggetto del giudizio" onclick="window.clearSingleFilter('oggetto_di_giudizio')">${model.fam_flow_dashboard.oggetto_di_giudizio}<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.tipologia_procedimento != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Tipologia Procedimento" onclick="window.clearSingleFilter('tipologia_procedimento')">${model.fam_flow_dashboard.tipologia_procedimento}<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.addebito) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Addebito" onclick="window.clearSingleFilter('addebito')">Addebito ${model.fam_flow_dashboard.addebito}<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.motivi_addebito_separazione != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Motivi di addebito della separazione" onclick="window.clearSingleFilter('motivi_addebito_separazione')">${model.fam_flow_dashboard.motivi_addebito_separazione}<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.regime_affidamento != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Regime di affidamento" onclick="window.clearSingleFilter('regime_affidamento')">${model.fam_flow_dashboard.regime_affidamento}<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.regime_mantenimento != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Regime di mantenimento" onclick="window.clearSingleFilter('regime_mantenimento')">${model.fam_flow_dashboard.regime_mantenimento}<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.regime_collocamento != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Regime di collocamento" onclick="window.clearSingleFilter('regime_collocamento')">${model.fam_flow_dashboard.regime_collocamento}<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.assegnazione_casa_familiare != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Assegnazione casa coniugale" onclick="window.clearSingleFilter('assegnazione_casa_familiare')">${model.fam_flow_dashboard.assegnazione_casa_familiare}<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.data_active) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Anno" onclick="window.clearSingleFilter('data_active')">${model.fam_flow_dashboard.data_da} - ${model.fam_flow_dashboard.data_a} <i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.id_localita != "") {
    let nomeCitta: string = model.fam_flow_dashboard.id_localita;
    let findCitta: KeyValoreStr | undefined = model.fam_lista_citta.find((x: KeyValoreStr): boolean => { return x.id_localita == model.fam_flow_dashboard.id_localita });

    if (findCitta) nomeCitta = findCitta.citta;

    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Città" onclick="window.clearSingleFilter('id_localita')">${nomeCitta}<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.giudice != "") {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Giudice" onclick="window.clearSingleFilter('giudice')">${model.fam_flow_dashboard.giudice}<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.durata_del_processo_notNull) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Durata del processo presente" onclick="window.clearSingleFilter('durata_del_processo_notNull')">Durata processo presente<i class="bi bi-x"></i></span>`;
  }
  if (model.fam_flow_dashboard.durata_del_processo_active) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Durata processo" onclick="window.clearSingleFilter('durata_del_processo_active')">Durata processo ${model.fam_flow_dashboard.durata_del_processo}<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.valore_assegno_mantenimento_active) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Valore assegno di mantenimento" onclick="window.clearSingleFilter('valore_assegno_mantenimento_active')">` +
      `Assegno di mantenimento ${model.fam_flow_dashboard.valore_assegno_mantenimento_da} - ${model.fam_flow_dashboard.valore_assegno_mantenimento_a}` + 
      `<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_active) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Valore assegno per il contributo al mantenimento" onclick="window.clearSingleFilter('valore_assegno_contributo_mantenimento_active')">` +
      `Assegno per il contributo al mantenimento ${model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_da} - ${model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_a}` + 
      `<i class="bi bi-x"></i></span>`;
  }

  if (model.fam_flow_dashboard.valore_assegno_divorzile_active) {
    filtersHtml.value += `<span class="badge rounded-pill text-bg-danger badge-filtro" title="Valore assegno divorzio" onclick="window.clearSingleFilter('valore_assegno_divorzile_active')">` +
      `Valore assegno divorzio ${model.fam_flow_dashboard.valore_assegno_divorzile_da} - ${model.fam_flow_dashboard.valore_assegno_divorzile_a}` + 
      `<i class="bi bi-x"></i></span>`;
  }
}

function updateChoices(): void {
  createChoiceCitta();
  if (choiceCitta) choiceCitta.setChoiceByValue(model.fam_flow_dashboard.id_localita);

  createChoiceGiudice();
  if (choiceGiudice) choiceGiudice.setChoiceByValue(model.fam_flow_dashboard.giudice);
}

window.addEventListener(
    "listAllRefresh",
    (event) => {
      updateComponents();
    }
);

// COMPONENTS CLICK ----------------------
function clickOnChart(name: string, value: any) {
  let doIt: boolean = true;

  if (value.toString().indexOf(" (") >= 0) {
    let valuespl = value.split(" (");
    value = valuespl[0];
  }

  if (name == "id_localita") {
    if (value == "Other" || value == "other" || value == "Altre" || value == "Altro" || value == "Altri" || value == "altre" || value == "altro" || value == "altri" ) {
      doIt = false;
    } else {
      let findCitta: KeyValoreStr | undefined = model.fam_lista_citta.find((x: KeyValoreStr): boolean => {return x.citta == value });
      if (findCitta) value = findCitta.id_localita;
    }
  }

  if (doIt) {
    //@ts-ignore
    model.fam_flow_dashboard[name] = value;

    changeDashboardAt(name);
  }
}
function clickOnBox(name: string, value: any) {
  let doIt: boolean = true;

  switch(name) {
    case "durata_del_processo":
      model.fam_flow_dashboard.durata_del_processo_notNull = true;

      break;
    case "valore_assegno_mantenimento":
      model.fam_flow_dashboard.valore_assegno_mantenimento_da = model.fam_flow_dashboard.valore_assegno_mantenimento_min;
      model.fam_flow_dashboard.valore_assegno_mantenimento_a = model.fam_flow_dashboard.valore_assegno_mantenimento_max;
      model.fam_flow_dashboard.valore_assegno_mantenimento_active = true;

      break;
    case "valore_assegno_contributo_mantenimento":
      model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_da = model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_min;
      model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_a = model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_max;
      model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_active = true;

      break;
    case "valore_assegno_divorzile":
      model.fam_flow_dashboard.valore_assegno_divorzile_da = model.fam_flow_dashboard.valore_assegno_divorzile_min;
      model.fam_flow_dashboard.valore_assegno_divorzile_a = model.fam_flow_dashboard.valore_assegno_divorzile_max;
      model.fam_flow_dashboard.valore_assegno_divorzile_active = true;
  }
  
  if (doIt) changeDashboardAt(name);
}

// SIDEBAR ----------------------
function toggleSidebar(): void {
  model.sidebarCollapsed = !model.sidebarCollapsed;
  let colSX: HTMLElement | null = document.getElementById("colonnaSX");
  let colDX: HTMLElement | null = document.getElementById("colonnaDX");
  if (model.sidebarCollapsed) {
    if (colSX) colSX.style.display = "none";
    if (colDX) {
      colDX.classList.remove("col-md-9", "col-lg-10");
      colDX.classList.add("col-12");
    }
  } else {
    if (colSX) colSX.style.display = "block";
    if (colDX) {
      colDX.classList.remove("col-12");
      colDX.classList.add("col-md-9", "col-lg-10");
    }
  }
}

// SLIDERS IN PAGE ----------------------
function disableActive(wtf: string): void {
  if (wtf == 'durata_del_processo') {
    model.fam_flow_dashboard.durata_del_processo_active = false;
  }
}

function manageActive(wtf: string): void {
  if (wtf == 'durata_del_processo') {
    model.fam_flow_dashboard.durata_del_processo_active = !model.fam_flow_dashboard.durata_del_processo_active;
  }
  
  changeDashboardAt(wtf);
}

// SLIDERS MINMAX + UPDATE ----------------------
const fromMinMaxSlider = (active: boolean, cid: string) => {
  //@ts-ignore
  model.fam_flow_dashboard[cid + "_active"] = active;

  changeDashboardAt(cid)
};

const minMaxSlider_assegno_mantenimento = ref(null);
const minMaxSlider_assegno_contributo_mantenimento = ref(null);
const minMaxSlider_assegno_divorzile = ref(null);
const minMaxSlider_data = ref(null);

const updateMinMaxSliders = (doSet: boolean = false): void => {
  try {
    if (doSet) {
      //@ts-ignore
      minMaxSlider_assegno_mantenimento.value.updateMinMaxSlider(model.fam_flow_dashboard.valore_assegno_mantenimento_da, model.fam_flow_dashboard.valore_assegno_mantenimento_a)
      //@ts-ignore
      minMaxSlider_assegno_contributo_mantenimento.value.updateMinMaxSlider(model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_da, model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_a);
      //@ts-ignore
      minMaxSlider_assegno_divorzile.value.updateMinMaxSlider(model.fam_flow_dashboard.valore_assegno_divorzile_da, model.fam_flow_dashboard.valore_assegno_divorzile_a);

      //@ts-ignore
      minMaxSlider_data.value.updateMinMaxSlider(model.fam_flow_dashboard.data_da, model.fam_flow_dashboard.data_a);
    } else {
      //@ts-ignore
      minMaxSlider_assegno_mantenimento.value.updateMinMaxSlider()
      //@ts-ignore
      minMaxSlider_assegno_contributo_mantenimento.value.updateMinMaxSlider();
      //@ts-ignore
      minMaxSlider_assegno_divorzile.value.updateMinMaxSlider();

      //@ts-ignore
      minMaxSlider_data.value.updateMinMaxSlider();
    }
  } catch (e) {
    //console.log("%%%%", e)
  }
}

// MAP + UPDATE ----------------------
let markers: Array<any> = [];
let map: any = undefined;

//@ts-ignore
let markersClickFunction = ({domEvent, latLng}) => {
  const {target} = domEvent;
  
  model.fam_flow_dashboard.id_localita = target.getAttribute("id_localita");

  changeDashboardAt("id_localita");
}
async function initMap() {
  //@ts-ignore
  const {Map} = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
  map = new Map(document.getElementById('map') as HTMLElement, {
    center: {lat: 42.06842343842517, lng: 13.251924413256154},
    zoom: 6,
    mapId: 'strategiaMap666'
  });
  updateMap();
}
initMap();
async function updateMap() {
  //@ts-ignore
  const {AdvancedMarkerElement} = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
  //google.maps.event.removeListener(clickFunction);
  markers.forEach((marker: any) => {
    //marker.removeListener(markersClickFunction);
    marker.setMap(null);
    marker.dispose(); //= null;
  })
  markers = [];

  model.fam_get_dashboard.totale_sentenze_per_tribunale.forEach((x: KeyValoreStrNum) => {
    const markerTag = document.createElement("div");
    markerTag.title = x.tribunale.toString();
    markerTag.className = "marker-tag";

    //eliminato il totale tra parentesi
    //markerTag.textContent = x.tribunale.toString() + " (" + x.tot.toString() + ")";
    markerTag.textContent = x.tribunale.toString();

    markerTag.setAttribute("id_localita", x.id_localita.toString());

    const marker = new AdvancedMarkerElement({
      map,
      position: {lat: Number(x.lat), lng: Number(x.long)},
      content: markerTag,
      title: x.tribunale.toString(),
      gmpClickable: true
    });
    marker.addListener("click", markersClickFunction);
    markers.push(marker);
  })
}

//DOCUMENTI
const paginazione_component = ref(null);
const paginazione_jump_component = ref(null);

function sintetizzaConAi(estremi: string, id_doc_master: number, docRigaIndex: number): void {
  gtagO({
    event: 'sintetizza AI',
    target: estremi
  })

  CommsFamiglia.getInstance().openSintesi('famiglia-dashboard', docRigaIndex, {
    id_doc_master: id_doc_master,
    username: model.user.profile.userId,
    crmid: model.user.profile.crmId
  });
}

function apriDettaglio(estremi: string = "", id_doc_master: number = -1, id_databanks: number = -1, id: string =""): void {
  gtagO({
    event: 'apri dettaglio',
    target: estremi,
    destinazione: model.abbonatoDejure ? 'dejure' : 'overlay'
  })

  let comms: CommsFamiglia = CommsFamiglia.getInstance();
  if (id_doc_master < 0) {
    if (!model.openingDejure) {
      let allIds: number[] = [];
      model.fam_get_dashboard.sentenze_paginate.forEach((item: KeyValoreStrNum) => {
        if (item.estremi) {
          allIds.push(item.id_doc_master as number)
        }
      })
      if (model.abbonatoDejure) {
        model.openingDejure = true;
        comms.callDejure(allIds, ()=> { model.openingDejure = false});
      }
    }
  } else {
    if (model.abbonatoDejure) {
      comms.openDettaglioDejure(id_doc_master, id_databanks, id);
    } else {
      comms.openDettaglioModal(id_doc_master, id_databanks, id);
    }
  }
}
const updatePaginazione = (): void => {
  try {
    //@ts-ignore
    paginazione_component.value.updatePaginazione();
    //@ts-ignore
    if (paginazione_jump_component && paginazione_jump_component.value) paginazione_jump_component.value.updatePaginazione();
  } catch (e) {
    //console.log("%%%%", e)
  }
}
//REPORT
function generaReport(): void {
  let sFiltriAttivi = "";

  if (model.fam_flow_dashboard.oggetto_di_giudizio != "") {
    sFiltriAttivi = "Oggetto di Giudizio";
  }
  if (model.fam_flow_dashboard.tipologia_procedimento) {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Tipologia procedimento";
  }
  if (model.fam_flow_dashboard.addebito != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Addebito";
  }
  if (model.fam_flow_dashboard.motivi_addebito_separazione != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Motivi addebito separazione";
  }
  if (model.fam_flow_dashboard.regime_affidamento != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Regime affidamento";
  }
  if (model.fam_flow_dashboard.regime_collocamento != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Regime collocamento";
  }
  if (model.fam_flow_dashboard.regime_mantenimento != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Regime mantenimento";
  }

  if (model.fam_flow_dashboard.durata_del_processo > 0) {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Durata Processo";
  }

  if (model.fam_flow_dashboard.id_localita != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Citta";
  }

  if (model.fam_flow_dashboard.giudice != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Giudice";
  }

  if (model.fam_flow_dashboard.assegnazione_casa_familiare != "") {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Assegnazione casa familiare";
  }
  if (model.fam_flow_dashboard.valore_assegno_divorzile_active) {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Valore assegno divorzio";
  }

  if (model.fam_flow_dashboard.valore_assegno_mantenimento_active) {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Valore assegno mantenimento";
  }

  if (model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_active) {
    if (sFiltriAttivi != "") { sFiltriAttivi += "|" }
    sFiltriAttivi += "Valore assegno contributo mantenimento";
  }

  PdfGenerator.fam_generatePdfDashboard("famiglia");

  gtagO({
    event: 'genera report',
    filtriAttivi: sFiltriAttivi
  })

}
</script>

<template>
  <div class="container-fluid dashboard-page">
    <div class="row">
      <!-- COLONNA SX -->
      <div id="colonnaSX" class="sidebar border border-right border-top-0 col-md-3 col-lg-2 p-0">
        <div tabindex="-1" id="sidebarMenu" aria-labelledby="sidebarMenuLabel">
          <div id="sidebarMenuInner" class="d-md-flex flex-column p-0 pt-lg-3 overflow-y-auto overflow-x-hidden">
            <!-- FILTRI ATTIVI -->
            <h6 class="sidebar-heading d-flex justify-content-between align-items-center px-3 mt-1 mb-1 text-body-secondary text-uppercase">
              <span>Filtri attivi</span>
              <a class="link-secondary" href="#" aria-label="" @click="clearAllFilters()" title="Pulisci tutti i filtri">
                <i class="bi bi-x-circle"></i>
              </a>
            </h6>
            <div class="px-2 filters-badges" v-html="filtersHtml"></div>

            <!-- FILTRI -->
            <hr class="my-3">
            <div class="itemFiltri">
              <label for="input_oggetto_di_giudizio">Oggetto del giudizio</label>

              <select class="form-select" id="input_oggetto_di_giudizio" @change="changeDashboardAt('oggetto_di_giudizio')" v-model="model.fam_flow_dashboard.oggetto_di_giudizio">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_oggetto_di_giudizio" :value="item" :key="'oggetto_di_giudizio_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_tipologia_procedimento">Tipologia Procedimento</label>

              <select class="form-select" id="input_tipologia_procedimento" @change="changeDashboardAt('tipologia_procedimento')" v-model="model.fam_flow_dashboard.tipologia_procedimento">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_tipologia_procedimento" :value="item" :key="'tipologia_procedimento_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_addebito">Addebito</label>

              <select class="form-select" id="input_addebito" @change="changeDashboardAt('addebito')" v-model="model.fam_flow_dashboard.addebito">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_addebito" :value="item" :key="'addebito_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_motivi_addebito_separazione">Motivi di addebito della separazione</label>

              <select class="form-select" id="input_motivi_addebito_separazione" @change="changeDashboardAt('motivi_addebito_separazione')" v-model="model.fam_flow_dashboard.motivi_addebito_separazione">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_motivi_addebito_separazione" :value="item" :key="'motivi_addebito_separazione_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_regime_affidamento">Regime di affidamento</label>

              <select class="form-select" id="input_regime_affidamento" @change="changeDashboardAt('regime_affidamento')" v-model="model.fam_flow_dashboard.regime_affidamento">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_regime_affidamento" :value="item" :key="'regime_affidamento_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_regime_collocamento">Regime di collocamento</label>

              <select class="form-select" id="input_regime_collocamento" @change="changeDashboardAt('regime_collocamento')" v-model="model.fam_flow_dashboard.regime_collocamento">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_regime_collocamento" :value="item" :key="'regime_collocamento_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_regime_mantenimento">Regime di mantenimento</label>

              <select class="form-select" id="input_regime_mantenimento" @change="changeDashboardAt('regime_mantenimento')" v-model="model.fam_flow_dashboard.regime_mantenimento">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_regime_mantenimento" :value="item" :key="'regime_mantenimento_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_assegnazione_casa_familiare">Assegnazione casa coniugale</label>

              <select class="form-select" id="input_assegnazione_casa_familiare" @change="changeDashboardAt('assegnazione_casa_familiare')" v-model="model.fam_flow_dashboard.assegnazione_casa_familiare">
                <option value="">Scegli...</option>
                <option v-for="(item) in Model.lista_assegnazione_casa_familiare" :value="item" :key="'assegnazione_casa_familiare_' + item">
                  {{item}}
                </option>
              </select>
            </div>

            <div class="itemFiltri">
              <label for="">Valore assegno di mantenimento</label>
              <div class="slider-cont">
                <MinMaxSlider ref="minMaxSlider_assegno_mantenimento"
                  v-model:min="model.fam_flow_dashboard.valore_assegno_mantenimento_min" v-model:max="model.fam_flow_dashboard.valore_assegno_mantenimento_max"
                  :step="Model.lista_valori_assegno_minMax[2]"
                  v-model:active="model.fam_flow_dashboard.valore_assegno_mantenimento_active"
                  v-model:min-value="model.fam_flow_dashboard.valore_assegno_mantenimento_da" v-model:max-value="model.fam_flow_dashboard.valore_assegno_mantenimento_a"
                  :cid="'valore_assegno_mantenimento'" :changeFunct="fromMinMaxSlider" :section="'famiglia'"
                />

                <div class="legenda">
                  <span v-html="model.fam_flow_dashboard.valore_assegno_mantenimento_active? '[A] ':'[N] '"></span>
                  <span v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.fam_flow_dashboard.valore_assegno_mantenimento_da)"></span>
                  &nbsp; <i class="bi bi-arrows"></i> &nbsp;
                  <span v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.fam_flow_dashboard.valore_assegno_mantenimento_a)"></span>
                </div>
              </div>
            </div>

            <div class="itemFiltri">
              <label for="">Valore assegno per il contributo al mantenimento</label>
              <div class="slider-cont">
                <MinMaxSlider ref="minMaxSlider_assegno_contributo_mantenimento"
                  v-model:min="model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_min" v-model:max="model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_max"
                  :step="Model.lista_valori_assegno_minMax[2]"
                  v-model:active="model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_active"
                  v-model:min-value="model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_da" v-model:max-value="model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_a"
                  :cid="'valore_assegno_contributo_mantenimento'" :changeFunct="fromMinMaxSlider" :section="'famiglia'"
                />

                <div class="legenda">
                  <span v-html="model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_active? '[A] ':'[N] '"></span>
                  <span v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_da)"></span>
                  &nbsp; <i class="bi bi-arrows"></i> &nbsp;
                  <span v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.fam_flow_dashboard.valore_assegno_contributo_mantenimento_a)"></span>
                </div>
              </div>
            </div>

            <div class="itemFiltri">
              <label for="">Valore assegno divorzio</label>
              <div class="slider-cont">
                <MinMaxSlider ref="minMaxSlider_assegno_divorzile"
                  v-model:min="model.fam_flow_dashboard.valore_assegno_divorzile_min" v-model:max="model.fam_flow_dashboard.valore_assegno_divorzile_max"
                  :step="Model.lista_valori_assegno_minMax[2]"
                  v-model:active="model.fam_flow_dashboard.valore_assegno_divorzile_active"
                  v-model:min-value="model.fam_flow_dashboard.valore_assegno_divorzile_da" v-model:max-value="model.fam_flow_dashboard.valore_assegno_divorzile_a"
                  :cid="'valore_assegno_divorzile'" :changeFunct="fromMinMaxSlider" :section="'famiglia'"
                />

                <div class="legenda">
                  <span v-html="model.fam_flow_dashboard.valore_assegno_divorzile_active? '[A] ':'[N] '"></span>
                  <span v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.fam_flow_dashboard.valore_assegno_divorzile_da)"></span>
                  &nbsp; <i class="bi bi-arrows"></i> &nbsp;
                  <span v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.fam_flow_dashboard.valore_assegno_divorzile_a)"></span>
                </div>
              </div>
            </div>

            <div class="itemFiltri">
              <label for="">Anno</label>
              <div class="slider-cont">
                <MinMaxSlider ref="minMaxSlider_data"
                  v-model:active="model.fam_flow_dashboard.data_active"
                  v-model:min="model.fam_flow_dashboard.data_min" v-model:max="model.fam_flow_dashboard.data_max"
                  :step="1"
                  v-model:min-value="model.fam_flow_dashboard.data_da" v-model:max-value="model.fam_flow_dashboard.data_a"
                  :cid="'data'" :changeFunct="fromMinMaxSlider" :section="'famiglia'"
                />

                <div class="legenda">
                  <span v-html="model.fam_flow_dashboard.data_active? '[A] ':'[N] '"></span>
                  <span v-html="model.fam_flow_dashboard.data_da"></span>
                  &nbsp; <i class="bi bi-arrows"></i> &nbsp;
                  <span v-html="model.fam_flow_dashboard.data_a"></span>
                </div>
              </div>
            </div>

            <div class="itemFiltri">
              <label for="input_id_localita">Città</label>
              <select class="form-select js-choice" id="input_id_localita" v-model="model.fam_flow_dashboard.id_localita" @change="changeDashboardAt('id_localita')">
              </select>
            </div>

            <div class="itemFiltri">
              <label for="input_giudice">Giudice</label>
              <select class="form-select js-choice" id="input_giudice" v-model="model.fam_flow_dashboard.giudice" @change="changeDashboardAt('giudice')">
              </select>
            </div>

            <!-- ALTRIFILTRI -->
            <hr class="my-3">
            <h6 class="sidebar-heading d-flex justify-content-between align-items-center px-3 mt-0 mb-1 text-body-secondary text-uppercase cursor-pointer" data-bs-target="#collapseAltriFiltri" data-bs-toggle="collapse">
              <span>Altri filtri</span>
              <span>
                <i class="bi bi-arrows-collapse"></i>
              </span>
            </h6>

            <div class="collapse show" id="collapseAltriFiltri">
              <hr class="my-3">

              <div class="itemFiltri">
                <label for="">Durata del processo</label>
                <div class="slider-cont">
                  <input @input="disableActive('durata_del_processo')" type="range" class="form-range range_input" min="0" max="10000" step="1" 
                    id="input_durata_del_processo" v-model="model.fam_flow_dashboard.durata_del_processo"/>
                  <span class="range_value">
                    <input @input="disableActive('durata_del_processo')" type="number" class="form-range" min="0" max="10000" step="1"
                      id="input_durata_del_processo2" v-model="model.fam_flow_dashboard.durata_del_processo"/>
                  </span>

                  <div class="form-check form-switch">
                    <button @click="manageActive('durata_del_processo')" class="btn btn-sm btn-primary btn-for-slider" v-if="!model.fam_flow_dashboard.durata_del_processo_active">
                      Applica
                    </button>
                    <button @click="manageActive('durata_del_processo')" class="btn btn-sm btn-danger btn-for-slider" v-else>
                      Rimuovi
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <hr class="my-3">
          </div>
        </div>
      </div>

        <!-- COLONNA DX -->
      <main id="colonnaDX" class="col-md-9 ms-sm-auto col-lg-10 px-md-4 mb-4 pt-1 colonnaDxFamiglia">
        <button id="toggleColonnaSX" class="btn" @click="toggleSidebar()" title="Mostra/Nascondi Filtri">
          <i class="bi bi-list"></i>
        </button>
      
        <!-- GRAFICI -->
        <div class="row">
          <div class="col-6 p-2">
            <div class="itemBox">
              <h4>Numero sentenze prese in esame</h4>
              <h5 v-html="model.fam_get_dashboard.totale_numero_sentenze.valore"></h5>
              <h6 v-if="model.fam_get_dashboard.totale_numero_sentenze?.valore > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
            </div>
          </div>

          <div class="col-6 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_durata_del_processo"
                data-bs-title="Questo dato ti permette di filtrare i processi in base alla loro durata media.
                <br>Clicca qui se vuoi filtrare le sentenze ottenendo solo quelle per cui è possibile risalire alla durata complessiva del procedimento.">
                <i class="bi bi-info-circle-fill"></i>
              </button>
              <h4 class="clickableBox" @click="clickOnBox('durata_del_processo', model.fam_get_dashboard.durata_del_processo.valore)">Durata processo</h4>
              <h5 v-html="model.fam_get_dashboard.durata_del_processo.valore" class="clickableBox" @click="clickOnBox('durata_del_processo', model.fam_get_dashboard.durata_del_processo.valore)"></h5>
              <h6 v-if="model.fam_get_dashboard.durata_del_processo?.valore > 0">Il 50% dei processi ha una durata in mesi superiore</h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
            </div>
          </div>

          <!-- Inizio prima riga -->
          <div class="col-4 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_oggetto_di_giudizio"
                data-bs-title="Questo grafico ti permette di scegliere tra le sentenze relative ai procedimenti di Separazione e a quelle relative allo scioglimento del 
                vincolo matrimoniale e alla cessazione degli effetti civili, entrambe ricomprese nell’etichetta Divorzio. Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Oggetto del giudizio</h4>
              <h6 v-if="model.fam_get_dashboard.oggetto_di_giudizio?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_oggetto_di_giudizio"></canvas>

              <div id="legend-container_oggetto_di_giudizio"></div>
            </div>
          </div>

          <div class="col-4 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_tipologia_procedimento"
                data-bs-title="Questo grafico ti permette di scegliere tra le diverse tipologie di procedimento. Puoi scegliere tra i procedimenti consensuali o 
                quelli giudiziali. Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Tipologia Procedimento</h4>
              <h6 v-if="model.fam_get_dashboard.tipologia_procedimento?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_tipologia_procedimento"></canvas>

              <div id="legend-container_tipologia_procedimento"></div>
            </div>
          </div>

          <div class="col-4 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_addebito"
                data-bs-title="Questo grafico ti permette di scegliere se valutare le sentenze in cui vi è addebito della separazione oppure no.
                Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Addebito</h4>
              <h6 v-if="model.fam_get_dashboard.addebito?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_addebito"></canvas>

              <div id="legend-container_addebito"></div>
            </div>
          </div>
          <!-- Fine prima riga -->

          <!-- Inizio seconda riga -->
          <div class="col-4 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_motivi_addebito_separazione"
                data-bs-title="Questo grafico ti permette di scegliere fra i motivi che hanno portato all’addebito della separazione. Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Motivi di addebito della separazione</h4>
              <h6 v-if="model.fam_get_dashboard.motivi_addebito_separazione?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_motivi_addebito_separazione"></canvas>

              <div id="legend-container_motivi_addebito_separazione"></div>
            </div>
          </div>

          <div class="col-4 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_regime_affidamento"
                data-bs-title="Questo grafico ti permette di selezionare la tipologia di affidamento dei figli nell’ambito della separazione. 
                Scegli il valore di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Regime di affidamento</h4>
              <h6 v-if="model.fam_get_dashboard.regime_affidamento?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_regime_affidamento"></canvas>

              <div id="legend-container_regime_affidamento"></div>
            </div>
          </div>

          <div class="col-4 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_regime_collocamento"
                data-bs-title="Il grafico permette di scegliere fra i diversi regimi di collocamento. Clicca sulla tipologia di regime per avere l'indicazione specifica.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Regime di collocamento</h4>
              <h6 v-if="model.fam_get_dashboard.regime_collocamento?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_regime_collocamento"></canvas>

              <div id="legend-container_regime_collocamento"></div>
            </div>
          </div>
          <!-- Fine seconda riga -->

          <!-- Inizio terza riga -->
          <div class="col-4 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_regime_mantenimento"
                data-bs-title="Il grafico permette di scegliere fra i due diversi regimi di mantenimento. Clicca sulla tipologia di regime per avere l'indicazione specifica.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Regime di mantenimento</h4>
              <h6 v-if="model.fam_get_dashboard.regime_mantenimento?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_regime_mantenimento"></canvas>

              <div id="legend-container_regime_mantenimento"></div>
            </div>
          </div>

          <div class="col-4 p-2">
            <div class="itemBox">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_assegnazione_casa_familiare"
                data-bs-title="Il grafico ti consente di sceglier il soggetto a cui è stata assegnata la casa coniugale per procedimento. Scegli il tuo filtro.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Assegnazione casa coniugale</h4>
              <h6 v-if="model.fam_get_dashboard.assegnazione_casa_familiare?.length > 0"></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <canvas id="canvas_assegnazione_casa_familiare"></canvas>

              <div id="legend-container_assegnazione_casa_familiare"></div>
            </div>
          </div>

          <div class="col-4 p-2">
            <div class="itemBox itemBoxH33">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_valore_assegno_mantenimento"
                data-bs-title="Individua il valore o un intervallo di valori dell'importo dell'assegno di mantenimento versato">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4 class="clickableBox" @click="clickOnBox('valore_assegno_mantenimento', model.fam_get_dashboard.valore_assegno_mantenimento.valore)">
                Assegno mantenimento
              </h4>
              <h5 v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.fam_get_dashboard.valore_assegno_mantenimento.valore)" 
                class="clickableBox" @click="clickOnBox('valore_assegno_mantenimento', model.fam_get_dashboard.valore_assegno_mantenimento.valore)"></h5>
              <h6 v-if="model.fam_get_dashboard.valore_assegno_mantenimento?.valore > 0">Il 50% degli importi ha un valore superiore a questa cifra</h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
            </div>
              
            <div class="itemBox itemBoxH33">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_valore_assegno_contributo_mantenimento"
                data-bs-title="Individua il valore o un intervallo di valori dell'importo del contributo versato per il mantenimento.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4 class="clickableBox" @click="clickOnBox('valore_assegno_contributo_mantenimento', model.fam_get_dashboard.valore_assegno_contributo_mantenimento.valore)">
                Contributo al mantenimento
              </h4>
              <h5 v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.fam_get_dashboard.valore_assegno_contributo_mantenimento.valore)" 
                class="clickableBox" @click="clickOnBox('valore_assegno_contributo_mantenimento', model.fam_get_dashboard.valore_assegno_contributo_mantenimento.valore)"></h5>
              <h6 v-if="model.fam_get_dashboard.valore_assegno_contributo_mantenimento?.valore > 0">Il 50% degli importi ha un valore superiore a questa cifra</h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
            </div>
            
            <div class="itemBox itemBoxH33">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_valore_assegno_divorzile"
                  data-bs-title="Individua il valore o un intervallo di valori dell’importo del contributo versato a titolo di assegno divorzile.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4 class="clickableBox" @click="clickOnBox('valore_assegno_divorzile', model.fam_get_dashboard.valore_assegno_divorzile.valore)">
                Assegno divorzile
              </h4>
              <h5 v-html="Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(model.fam_get_dashboard.valore_assegno_divorzile.valore)" 
                class="clickableBox" @click="clickOnBox('valore_assegno_divorzile', model.fam_get_dashboard.valore_assegno_divorzile.valore)"></h5>
              <h6 v-if="model.fam_get_dashboard.valore_assegno_divorzile?.valore > 0">Il 50% degli importi ha un valore superiore a questa cifra</h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
            </div>
          </div>
          <!-- Fine terza riga -->
          
          <div class="col-6 p-2">
            <div class="itemBox itemBoxDouble">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_durata_del_processo_per_singola_citta"
                data-bs-title="Questo grafico ti permette di verificare la durata di un processo in base al luogo in cui si è svolto.<br> Clicca sulla singola città per 
                avere l’indicazione diretta dell’andamento in quel foro.<br>Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Durata del processo per singola città</h4>
              <h6 v-if="model.fam_get_dashboard.durata_del_processo_per_singola_citta?.length > 0">
                Il 50% dei processi ha avuto una durata in mesi superiore al valore indicato
              </h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
              <canvas id="canvas_durata_del_processo_per_singola_citta"></canvas>
            </div>

            <div class="itemBox itemBoxDouble">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true" id="tooltip_esito_del_processo_per_citta"
                data-bs-title="Il grafico riporta la distribuzione per città degli esiti dei processi oggetto di analisi.<br>Clicca sulla tipologia di esito del processo 
                per avere l’indicazione specifica rispetto al singolo foro.<br>Scegli il filtro di tuo interesse.">
                <i class="bi bi-info-circle-fill"></i>
              </button>

              <h4>Oggetto del giudizio per città</h4>
              <h6 v-if="model.fam_get_dashboard.esito_del_processo_per_citta?.length > 0">
                Distribuzione oggetto del giudizio
              </h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>
              <canvas id="canvas_esito_del_processo_per_citta"></canvas>
            </div>
          </div>

          <div class="col-6 p-2">
            <div class="itemBox itemBoxMap">
              <button type="button" class="btn btn-tooltip" data-bs-toggle="tooltip" data-bs-html="true"
                      data-bs-title="Clicca sulla singola città per applicare il relativo filtro">
                <i class="bi bi-info-circle-fill"></i>
              </button>
              <h4>Mappa</h4>
              <h6 v-if="model.fam_get_dashboard.totale_sentenze_per_tribunale?.length > 0"><!-- Lorem ipsum --></h6>
              <h6 v-else>Dato non disponibile con i filtri attuali.</h6>

              <div id="map"></div>
            </div>
          </div>

          <div class="col-12 p-2">
            <div class="itemBox itemBoxLast">
              <h4>Sentenze</h4>

              <div class="table-responsive small table-documenti" v-if="model.fam_get_dashboard.sentenze_paginate && model.fam_get_dashboard.sentenze_paginate.length && model.fam_get_dashboard.sentenze_paginate.length > 0">
                <table class="table table-striped table-sm">
                  <tbody>
                  <template v-for="(item, index) in model.fam_get_dashboard.sentenze_paginate">

                    <tr v-if="item.estremi" :id="'docRiga-famiglia-dashboard-'+index">
                      <!-- td><em>{{ (new Date(item.date)).toLocaleDateString() }}</em></td -->
                      <td>{{item.estremi}}</td>
                      <td class="text-right">
                        <a href="javascript:void(0)" @click="sintetizzaConAi(item.estremi as string, item.id_doc_master as number, index)" v-if="item.tipologia_doc != 136">
                          <i class="bi bi-stars"></i> Sintetizza con AI
                        </a>
                        <span v-else> </span>
                      </td>
                      <td class="text-right">
                        <a href="javascript:void(0)" @click="apriDettaglio(item.estremi as string, item.id_doc_master as number, item.id_databanks as number, item.id as string)">
                          <i class="bi bi-link"></i> Apri dettaglio
                        </a>
                      </td>
                    </tr>
                  </template>
                  </tbody>
                </table>
              </div>

              <div class="row" v-if="model.fam_get_dashboard.sentenze_paginate && model.fam_get_dashboard.sentenze_paginate.length && model.fam_get_dashboard.sentenze_paginate.length > 0">
                <div class="col-12 col-sm-6 col-xl-4 paginazione-azione">
                  <button class="btn btn-sm btn-primary" @click="apriDettaglio()" v-if="model.abbonatoDejure">
                    <i class="bi bi-box-arrow-up-right" v-if="!model.openingDejure"></i> 
                    <div v-else class="spinner-border" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>&nbsp;  
                    Approfondisci su DeJure
                  </button>

                  <button class="btn btn-sm btn-error" @click="generaReport()" v-if="model.fam_flow_dashboard.oggetto_di_giudizio != ''">
                    <i class="bi bi-download" v-if="!model.generatingPdf"></i>
                    <div v-else class="spinner-border" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    &nbsp; Genera report
                  </button>

                </div>
                <div class="col-12 col-sm-6 col-xl-3 paginazione-label">
                  <b>{{ model.fam_flow_dashboard.paginazione_tot }}</b> Elementi Totali |
                  Pagina <b>{{ (model.fam_flow_dashboard.paginazione_start + 1) }}</b> di
                  <b>{{ (model.fam_flow_dashboard.paginazione_pages + 1) }}</b>
                </div>
                <div class="col-12 col-sm-12 col-xl-5">
                  <PaginazioneDashboard ref="paginazione_component" @recalc="callQueryPaginazione()"></PaginazioneDashboard>
                </div>

                <!-- principi di diritto -->
                <div class="col-12 mt-3" v-if="model.abbonatoDejure && model.fam_get_dashboard.sentenze_paginate_jump_display && model.fam_get_dashboard.sentenze_paginate_jump_display.length && model.fam_get_dashboard.sentenze_paginate_jump_display.length > 0">
                  <h4>Principi di diritto </h4>

                  <div class="table-responsive small table-documenti">
                    <table class="table table-striped table-sm">
                      <tbody>
                      <template v-for="(item, index) in model.fam_get_dashboard.sentenze_paginate_jump_display">
                        <tr v-if="item.estremi || item.id_doc_master" :id="'docRiga-famiglia-dashboard-jump-'+index">
                          <td v-if="item.estremi">{{item.estremi}}</td>
                          <td v-else> {{item.id_doc_master}}</td>
                          <td class="text-right">
                            <a href="javascript:void(0)" @click="apriDettaglio(item.estremi as string, item.id_doc_master as number, item.id_databanks as number, item.id as string)">
                              <i class="bi bi-link"></i> Apri dettaglio
                            </a>
                          </td>
                        </tr>
                      </template>
                      </tbody>
                    </table>
                  </div>

                  <div class="row">
                    <div class="col-12 col-sm-6 col-xl-4 paginazione-azione">

                    </div>
                    <div class="col-12 col-sm-6 col-xl-3 paginazione-label">
                      <b>{{ model.fam_flow_dashboard.diritto_paginazione_tot }}</b> Elementi Totali |
                      Pagina <b>{{ (model.fam_flow_dashboard.diritto_paginazione_start + 1) }}</b> di
                      <b>{{ (model.fam_flow_dashboard.diritto_paginazione_pages + 1) }}</b>
                    </div>
                    <div class="col-12 col-sm-12 col-xl-5">
                      <PaginazioneDashboardJump ref="paginazione_jump_component"></PaginazioneDashboardJump>
                    </div>
                  </div>

                </div>

              </div>
              <div class="row" v-else>
                <div class="col-12">
                  Non sono presenti sentenze
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>